import React from 'react';
import { IconSearch, IconBook, IconSparkle, IconPeople } from './Icons';
import ScentdLogo from './ScentdLogo';

type NavTab = 'discover' | 'journal' | 'myScent' | 'follow';

interface UserBadge {
  firstName: string;
  lastInitial: string;
  memberNumber: number;
  archetypeName?: string;
  archetypeColor?: string;
  pendingRequests?: number;
}

interface NavBarProps {
  activeTab: NavTab;
  onTabChange: (tab: NavTab) => void;
  onLogoClick: () => void;
  onBadgeClick?: () => void;
  userBadge?: UserBadge;
}

const tabs: Array<{ id: NavTab; label: string; shortLabel?: string; Icon: React.FC<{ size?: number; className?: string }> }> = [
  { id: 'discover', label: 'Discover', Icon: IconSearch },
  { id: 'journal', label: 'Journal', Icon: IconBook },
  { id: 'myScent', label: 'My Scent', Icon: IconSparkle },
  { id: 'follow', label: 'Follow My Scent', shortLabel: 'Follow', Icon: IconPeople },
];

const NavBar: React.FC<NavBarProps> = ({ activeTab, onTabChange, onLogoClick, onBadgeClick, userBadge }) => {
  const activeIndex = tabs.findIndex(t => t.id === activeTab);

  return (
    <>
      {/* Header */}
      <header className="border-b border-cream-dark px-6 py-4 flex justify-between items-center bg-cream sticky top-0 z-30">
        <button
          onClick={onLogoClick}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
        >
          <ScentdLogo size={32} />
          <span className="font-serif text-2xl text-charcoal">Scentd</span>
        </button>

        {/* Desktop nav with sliding underline */}
        <nav className="hidden md:flex gap-8 items-center relative">
          {tabs.map((tab, i) => {
            const pendingCount = tab.id === 'follow' ? (userBadge?.pendingRequests ?? 0) : 0;
            return (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={`text-xs tracking-widest uppercase pb-1 transition-colors relative flex items-center gap-1.5 ${
                  activeTab === tab.id ? 'text-charcoal' : 'text-stone-light hover:text-stone'
                }`}
              >
                {tab.label}
                {pendingCount > 0 && (
                  <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-400 text-white" style={{ fontSize: '9px', fontFamily: 'system-ui' }}>
                    {pendingCount}
                  </span>
                )}
                {activeTab === tab.id && (
                  <span className="absolute bottom-0 left-0 right-0 h-px bg-charcoal" />
                )}
              </button>
            );
          })}
        </nav>

        {userBadge ? (
          <button
            onClick={onBadgeClick}
            className="text-right flex-shrink-0 hover:opacity-75 transition-opacity"
          >
            <p className="text-xs font-medium text-charcoal leading-tight">
              {userBadge.firstName} {userBadge.lastInitial}.
              <span className="text-stone-light font-normal"> · #{String(userBadge.memberNumber).padStart(4, '0')}</span>
            </p>
            {userBadge.archetypeName && (
              <p className="text-xs text-stone-light leading-tight mt-0.5 flex items-center justify-end gap-1">
                <span
                  className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ background: userBadge.archetypeColor }}
                />
                {userBadge.archetypeName}
              </p>
            )}
          </button>
        ) : (
          <div className="w-9" />
        )}
      </header>

      {/* Bottom nav — mobile only */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-cream border-t border-cream-dark z-30">
        <div className="flex">
          {tabs.map(tab => {
            const pendingCount = tab.id === 'follow' ? (userBadge?.pendingRequests ?? 0) : 0;
            return (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={`flex-1 py-3 flex flex-col items-center gap-1 transition-colors ${
                  activeTab === tab.id ? 'text-charcoal' : 'text-stone-light'
                }`}
              >
                <div className="relative">
                  <tab.Icon size={20} />
                  {pendingCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-0.5 rounded-full bg-red-400 text-white flex items-center justify-center" style={{ fontSize: '8px', fontFamily: 'system-ui' }}>
                      {pendingCount}
                    </span>
                  )}
                </div>
                <span style={{ fontSize: '9px' }} className="tracking-widest uppercase">
                  {tab.shortLabel ?? tab.label}
                </span>
              </button>
            );
          })}
        </div>
        {/* sliding indicator at top of bottom nav */}
        <div
          className="absolute top-0 h-0.5 bg-charcoal transition-all duration-300"
          style={{
            width: `${100 / tabs.length}%`,
            left: `${(activeIndex / tabs.length) * 100}%`,
          }}
        />
      </nav>
    </>
  );
};

export default NavBar;
